import { useEffect, useState } from 'react';
import { formatUsdValue } from '@chainflip/utils/number';
import classNames from 'classnames';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { TokenWithChainByAsset } from '@/shared/components/index';
import { useValidatedTokenAmountInput, useValidatedTokenAmount } from '@/shared/hooks';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import { type TokenAmount } from '@/shared/utils';

export interface TokenInputFieldProps {
  token: ChainflipToken;
  value: TokenAmount | undefined;
  onChange: (value: TokenAmount | undefined) => void;
  balance?: TokenAmount;
  maxAmount?: TokenAmount;
  subtext?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  displayChain?: boolean;
  focusOnMount?: boolean;
  onInput?: () => void;
}

export default function TokenInputField({
  token,
  value,
  onChange,
  balance,
  maxAmount,
  subtext,
  label,
  error,
  disabled = false,
  displayChain = true,
  focusOnMount = false,
  onInput,
}: TokenInputFieldProps): JSX.Element {
  const { price } = useTokenPrice(token);
  const effectiveMaxAmount = maxAmount ?? balance;
  const [maxAmountError, setMaxAmountError] = useState<string>();
  const displayedError = error ?? maxAmountError;

  const {
    onChange: onInputChange,
    value: inputValue,
    setValue: setInputValue,
  } = useValidatedTokenAmountInput({
    token,
    initialValue: value?.toFixed() ?? '',
  });
  const validatedAmount = useValidatedTokenAmount(inputValue, token);
  const usdAmount = validatedAmount?.mul(price ?? 0).toNumber() ?? 0;
  const displayedSubtext = subtext ?? formatUsdValue(usdAmount);

  useEffect(() => {
    if (effectiveMaxAmount && validatedAmount && validatedAmount.gt(effectiveMaxAmount)) {
      onChange(undefined);
      setMaxAmountError(`Amount exceeds ${maxAmount ? 'maximum' : 'balance'}`);
    } else {
      onChange(validatedAmount);
      setMaxAmountError(undefined);
    }
  }, [validatedAmount, effectiveMaxAmount, onChange]);

  return (
    <div className="w-full">
      <div
        className={classNames(
          'relative flex h-[4.5rem] flex-row rounded-md',
          'border border-cf-gray-4 bg-cf-gray-3-5 text-cf-light-4',
          displayedError && 'border-cf-red-1',
          disabled
            ? 'cursor-not-allowed [&>*]:pointer-events-none'
            : 'group transition hover:bg-cf-gray-4', // apply group-hover effect only when enabled
        )}
      >
        <div className="absolute left-2 top-[-10px] z-10 select-none px-[6px] text-12 text-cf-light-2">
          <div>{label}</div>
          <div className="absolute inset-x-0 bottom-0 top-[9px] -z-10 bg-cf-gray-3-5 transition group-hover:bg-cf-gray-4" />
        </div>
        <div className="relative w-0 grow">
          {effectiveMaxAmount?.gt(0) && !effectiveMaxAmount?.eq(validatedAmount) && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-cf-gray-4 bg-cf-gray-2 px-1.5 py-0.5 font-aeonikMono text-10 uppercase opacity-0 transition duration-100 ease-out hover:bg-cf-gray-3 hover:text-cf-white group-focus-within:opacity-100 group-hover:opacity-100"
              onClick={() => {
                setInputValue(effectiveMaxAmount.toFixed());
              }}
              type="button"
              disabled={disabled}
            >
              Max
            </button>
          )}
          <input
            placeholder="0"
            type="text"
            inputMode="decimal"
            className={classNames(
              'peer h-full w-full bg-transparent pl-3 pr-2 font-aeonikMono text-20 text-white outline-none placeholder:text-cf-light-2',
              displayedSubtext && 'pb-4',
            )}
            value={inputValue}
            onChange={onInputChange}
            onInput={() => onInput?.()}
            onBlur={() => {
              if (validatedAmount) {
                setInputValue(validatedAmount.eq(0) ? '' : validatedAmount.toFixed());
              }
            }}
            disabled={disabled}
            autoFocus={focusOnMount}
          />
          {displayedSubtext && (
            <span className="absolute bottom-0 left-0 pb-2.5 pl-3 font-aeonikMono text-12 text-cf-light-3/50">
              {displayedSubtext}
            </span>
          )}
        </div>
        <div
          className={classNames(
            'flex items-center justify-center py-4 pr-5',
            displayChain ? 'gap-3' : 'gap-2',
          )}
        >
          <TokenWithChainByAsset size="medium" asset={token.chainflipId} />
          <div className="ml-1 flex flex-col items-start">
            <div className="font-aeonikBold text-14 uppercase text-white">{token.symbol}</div>
            {displayChain && (
              <span className="-mt-0.5 w-full truncate text-left text-12 text-cf-light-1">
                on {token.chain.name}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center justify-between pt-2">
        <div className="text-12 text-cf-red-1">
          {displayedError}
          &nbsp;{/* prevent layout jump when error appears */}
        </div>
        {balance && (
          <div className="text-right font-aeonikMono text-12 text-cf-light-1">
            Balance:{' '}
            <span className="whitespace-nowrap text-cf-white">
              {balance.toFormatted()} {token.symbol}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
